import { memo, useCallback, useEffect, useState } from 'react';
import {
  FILTER_SLIDEOUT_CHEVRON_BUTTON,
  FILTER_SLIDEOUT_CLEAR_BUTTON,
  FILTER_SLIDEOUT_COUNT_BADGE,
  FILTER_SLIDEOUT_SECTION,
  FILTER_SLIDEOUT_SECTION_DISABLED,
  FILTER_SLIDEOUT_SECTION_HEADER,
  FILTER_SLIDEOUT_SECTION_HEADER_BUTTON,
} from './filterSlideoutTokens.js';
import { joinClasses } from './filterSlideout.utils.js';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shared collapsible frame for every filter section.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {import('react').ReactNode} props.children
 * @param {boolean} [props.defaultExpanded=true]
 * @param {boolean} [props.disabled=false]
 */
const FilterSlideoutSection = ({
  title,
  children,
  defaultExpanded = true,
  disabled = false,
  activeCount = 0,
  onClear,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasActiveFilter = activeCount > 0;

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const handleToggle = useCallback(() => {
    if (!disabled) setExpanded((value) => !value);
  }, [disabled]);

  return (
    <section
      className={joinClasses(
        FILTER_SLIDEOUT_SECTION,
        disabled ? FILTER_SLIDEOUT_SECTION_DISABLED : '',
      )}
      aria-disabled={disabled || undefined}
    >
      <div className={FILTER_SLIDEOUT_SECTION_HEADER}>
        <button
          type="button"
          className={FILTER_SLIDEOUT_SECTION_HEADER_BUTTON}
          onClick={handleToggle}
          aria-expanded={expanded}
          disabled={disabled}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="min-w-0 truncate">{title}</span>
            {hasActiveFilter && !expanded ? (
              <span className={FILTER_SLIDEOUT_COUNT_BADGE}>{activeCount}</span>
            ) : null}
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          {hasActiveFilter && onClear ? (
            <button
              type="button"
              className={FILTER_SLIDEOUT_CLEAR_BUTTON}
              onClick={onClear}
              disabled={disabled}
            >
              Clear
            </button>
          ) : null}
          <button
            type="button"
            className={FILTER_SLIDEOUT_CHEVRON_BUTTON}
            onClick={handleToggle}
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
            disabled={disabled}
          >
            {expanded ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
          </button>
        </div>
      </div>

      {expanded ? <div>{children}</div> : null}
    </section>
  );
};

export default memo(FilterSlideoutSection);
